import React, { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, ArrowDownCircle, ArrowUpCircle } from "lucide-react";
import { api, fmtDate } from "@/lib/apiClient";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const rp = (v) => `Rp ${Number(v || 0).toLocaleString("id-ID")}`;
const today = () => new Date().toISOString().slice(0, 10);
const KAT = ["SPAREPART", "BAHAN"];

const Field = ({ label, children }) => (
  <div>
    <span className="text-[10px] uppercase tracking-[0.16em] text-slate-500">{label}</span>
    <div className="mt-1">{children}</div>
  </div>
);

const Card = ({ label, value, warna }) => (
  <div className="glass rounded-2xl p-5">
    <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500">{label}</p>
    <div className={`mt-2 font-display text-2xl font-light ${warna}`}>{value}</div>
  </div>
);

const sel = "h-11 rounded-xl bg-slate-900/70 border border-white/10 px-3 text-sm outline-none";

/** Klik A3+ — entri bahan (SPAREPART/BAHAN), stok masuk/keluar, dan rekap khusus menu ini. */
export const A3Board = () => {
  const [mats, setMats] = useState([]);
  const [form, setForm] = useState({ kategori: "SPAREPART", jenis: "", merk: "", ukuran: "", harga: "" });
  const [mv, setMv] = useState({ material_id: "", jenis: "masuk", qty: "", tanggal: today(), catatan: "" });
  const [led, setLed] = useState({ rows: [], total_masuk: 0, total_keluar: 0, saldo: 0 });
  const [inc, setInc] = useState({ nominal: "", keterangan: "", tanggal: today(), kategori: "SPAREPART" });
  const [jenisFilter, setJenisFilter] = useState("ALL");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [m, l] = await Promise.all([
        api.get("/a3/materials"),
        api.get("/a3/ledger", { params: jenisFilter === "ALL" ? {} : { jenis: jenisFilter } }),
      ]);
      setMats(m.data);
      setLed(l.data);
    } catch {
      toast.error("Gagal memuat data Klik A3+");
    }
  }, [jenisFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (fn, okMsg) => {
    setBusy(true);
    try {
      await fn();
      toast.success(okMsg);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="a3-board">
      <div className="glass rounded-2xl p-5 md:p-6 space-y-4">
        <h4 className="font-display text-sm font-semibold uppercase tracking-wider text-slate-200">
          Entri Bahan Baru Klik A3+
        </h4>
        <div className="flex flex-wrap items-end gap-3">
          <Field label="Kategori">
            <select
              data-testid="a3-kategori-select"
              value={form.kategori}
              onChange={(e) => setForm({ ...form, kategori: e.target.value })}
              className={sel}
            >
              {KAT.map((k) => (
                <option key={k} value={k}>{k}</option>
              ))}
            </select>
          </Field>
          {[["jenis", "Jenis"], ["merk", "Merk"], ["ukuran", "Ukuran / Satuan"], ["harga", "Harga (Rp)"]].map(
            ([k, label]) => (
              <Field key={k} label={label}>
                <Input
                  data-testid={`a3-${k}-input`}
                  value={form[k]}
                  onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                  className="h-11 w-40 bg-slate-900/70 border-white/10"
                />
              </Field>
            )
          )}
          <Button
            data-testid="a3-material-submit"
            disabled={busy || !form.jenis.trim()}
            onClick={() =>
              act(async () => {
                await api.post("/a3/materials", { ...form, harga: Number(form.harga) || 0 });
                setForm({ kategori: form.kategori, jenis: "", merk: "", ukuran: "", harga: "" });
              }, "Bahan Klik A3+ ditambahkan")
            }
            className="h-11 rounded-xl bg-teal-600 hover:bg-teal-500 px-5"
          >
            <Plus className="w-4 h-4 mr-2" /> Simpan Bahan
          </Button>
        </div>

        <div className="overflow-x-auto rounded-xl border border-white/10">
          <table className="w-full text-xs">
            <thead className="bg-slate-900/80 text-slate-400 uppercase text-[10px] tracking-wider">
              <tr>
                {["Kategori", "Jenis", "Merk", "Ukuran", "Harga", "Stok", "User Entri", ""].map((h) => (
                  <th key={h} className="px-3 py-2 text-left font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody data-testid="a3-material-list">
              {mats.length === 0 && (
                <tr><td colSpan={8} className="px-3 py-6 text-center text-slate-500">Belum ada bahan Klik A3+.</td></tr>
              )}
              {mats.map((m) => (
                <tr key={m.id} className="border-t border-white/5">
                  <td className="px-3 py-2 text-slate-200">{m.kategori}</td>
                  <td className="px-3 py-2">{m.jenis}</td>
                  <td className="px-3 py-2 text-slate-400">{m.merk || "-"}</td>
                  <td className="px-3 py-2 text-slate-400">{m.ukuran || "-"}</td>
                  <td className="px-3 py-2">{rp(m.harga)}</td>
                  <td className="px-3 py-2 font-semibold text-teal-300">{m.stok}</td>
                  <td className="px-3 py-2 text-slate-500">{m.created_by}</td>
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      data-testid={`a3-material-delete-${m.id}`}
                      onClick={() => act(() => api.delete(`/a3/materials/${m.id}`), "Bahan dihapus")}
                      className="rounded-lg border border-rose-500/25 bg-rose-500/10 p-1.5 text-rose-300"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="glass rounded-2xl p-5 md:p-6 space-y-3" data-testid="a3-move-form">
        <h4 className="font-display text-sm font-semibold uppercase tracking-wider text-slate-200">
          Stok Masuk / Keluar Klik A3+
        </h4>
        <div className="flex flex-wrap items-end gap-3">
          <Field label="Bahan">
            <select
              data-testid="a3-move-material"
              value={mv.material_id}
              onChange={(e) => setMv({ ...mv, material_id: e.target.value })}
              className={`${sel} min-w-[240px]`}
            >
              <option value="">— pilih bahan —</option>
              {mats.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.kategori} · {m.jenis} (stok {m.stok})
                </option>
              ))}
            </select>
          </Field>
          <Field label="Mutasi">
            <select
              data-testid="a3-move-jenis"
              value={mv.jenis}
              onChange={(e) => setMv({ ...mv, jenis: e.target.value })}
              className={sel}
            >
              <option value="masuk">Stok Masuk</option>
              <option value="keluar">Stok Keluar</option>
            </select>
          </Field>
          <Field label="Jumlah">
            <Input data-testid="a3-move-qty" value={mv.qty} onChange={(e) => setMv({ ...mv, qty: e.target.value })} className="h-11 w-24 bg-slate-900/70 border-white/10" />
          </Field>
          <Field label="Tanggal">
            <Input data-testid="a3-move-date" type="date" value={mv.tanggal} onChange={(e) => setMv({ ...mv, tanggal: e.target.value })} className="h-11 bg-slate-900/70 border-white/10" />
          </Field>
          <Field label="Keterangan">
            <Input data-testid="a3-move-note" value={mv.catatan} onChange={(e) => setMv({ ...mv, catatan: e.target.value })} className="h-11 w-52 bg-slate-900/70 border-white/10" />
          </Field>
          <Button
            data-testid="a3-move-submit"
            disabled={busy || !mv.material_id || !mv.qty}
            onClick={() =>
              act(async () => {
                await api.post("/a3/stock-moves", { ...mv, qty: Number(mv.qty) || 0 });
                setMv({ ...mv, qty: "", catatan: "" });
              }, `Stok ${mv.jenis} Klik A3+ tercatat`)
            }
            className={`h-11 rounded-xl px-5 ${mv.jenis === "masuk" ? "bg-emerald-600 hover:bg-emerald-500" : "bg-rose-600 hover:bg-rose-500"}`}
          >
            {mv.jenis === "masuk" ? <ArrowDownCircle className="w-4 h-4 mr-2" /> : <ArrowUpCircle className="w-4 h-4 mr-2" />}
            Simpan Mutasi
          </Button>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <Card label="Total Pemasukan" value={rp(led.total_masuk)} warna="text-emerald-300" />
        <Card label="Total Pengeluaran" value={rp(led.total_keluar)} warna="text-rose-300" />
        <Card label="Saldo Klik A3+" value={rp(led.saldo)} warna="text-amber-300" />
      </div>

      <div className="glass rounded-2xl p-5 md:p-6 space-y-3" data-testid="a3-recap">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <h4 className="font-display text-sm font-semibold uppercase tracking-wider text-slate-200">
            Rekap Klik A3+
          </h4>
          <div className="flex flex-wrap items-end gap-3">
            <Field label="Filter Jenis">
              <select
                data-testid="a3-recap-filter"
                value={jenisFilter}
                onChange={(e) => setJenisFilter(e.target.value)}
                className="h-10 rounded-xl bg-slate-900/70 border border-white/10 px-3 text-sm outline-none"
              >
                <option value="ALL">Semua</option>
                <option value="masuk">Pemasukan</option>
                <option value="keluar">Pengeluaran</option>
              </select>
            </Field>
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-3 rounded-xl border border-white/10 bg-slate-900/50 p-3">
          <span className="text-[10px] uppercase tracking-wider text-slate-500">Entri pemasukan manual</span>
          <Input data-testid="a3-income-nominal" placeholder="Nominal" value={inc.nominal} onChange={(e) => setInc({ ...inc, nominal: e.target.value })} className="h-10 w-32 bg-slate-900/70 border-white/10" />
          <Input data-testid="a3-income-note" placeholder="Keterangan" value={inc.keterangan} onChange={(e) => setInc({ ...inc, keterangan: e.target.value })} className="h-10 w-56 bg-slate-900/70 border-white/10" />
          <Input data-testid="a3-income-date" type="date" value={inc.tanggal} onChange={(e) => setInc({ ...inc, tanggal: e.target.value })} className="h-10 bg-slate-900/70 border-white/10" />
          <Button
            data-testid="a3-income-submit"
            disabled={busy || !inc.nominal}
            onClick={() =>
              act(async () => {
                await api.post("/a3/ledger", { ...inc, jenis: "masuk", nominal: Number(inc.nominal) || 0 });
                setInc({ ...inc, nominal: "", keterangan: "" });
              }, "Pemasukan Klik A3+ tercatat")
            }
            className="h-10 rounded-xl bg-emerald-600 hover:bg-emerald-500 px-4 text-xs font-semibold"
          >
            Simpan Pemasukan
          </Button>
        </div>

        <div className="overflow-x-auto rounded-xl border border-white/10 max-h-80 overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-900/80 text-slate-400 uppercase text-[10px] tracking-wider sticky top-0">
              <tr>
                {["Nomor", "Tanggal", "Jenis", "Kategori", "Nominal", "Keterangan", "Sumber", "User"].map((h) => (
                  <th key={h} className="px-3 py-2 text-left font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody data-testid="a3-recap-list">
              {led.rows.length === 0 && (
                <tr><td colSpan={8} className="px-3 py-6 text-center text-slate-500">Belum ada transaksi Klik A3+.</td></tr>
              )}
              {led.rows.map((r) => (
                <tr key={r.id} className="border-t border-white/5">
                  <td className="px-3 py-2 text-slate-300">{r.nomor}</td>
                  <td className="px-3 py-2 text-slate-400">{fmtDate(r.tanggal)}</td>
                  <td className="px-3 py-2">
                    <span className={`rounded-lg px-2 py-1 text-[10px] font-semibold uppercase ${r.jenis === "masuk" ? "bg-emerald-500/15 text-emerald-300" : "bg-rose-500/15 text-rose-300"}`}>
                      {r.jenis}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-slate-400">{r.kategori || "-"}</td>
                  <td className="px-3 py-2 font-semibold">{rp(r.nominal)}</td>
                  <td className="px-3 py-2 text-slate-400">{r.keterangan || "-"}</td>
                  <td className="px-3 py-2 text-[10px] uppercase text-slate-500">{r.auto ? "otomatis" : "manual"}</td>
                  <td className="px-3 py-2 text-slate-500">{r.by}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

/** Pemasukan & Pengeluaran Gudang — ledger khusus Papan Kumendan. */
export const GudangBoard = () => {
  const [led, setLed] = useState({ rows: [], total_masuk: 0, total_keluar: 0, saldo: 0 });
  const [range, setRange] = useState({ start: "", end: "" });
  const [form, setForm] = useState({ jenis: "keluar", nominal: "", keterangan: "", tanggal: today(), kategori: "GUDANG" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/gudang/ledger", {
        params: { ...(range.start ? { start: range.start } : {}), ...(range.end ? { end: range.end } : {}) },
      });
      setLed(data);
    } catch {
      toast.error("Gagal memuat data gudang");
    }
  }, [range]);

  useEffect(() => {
    load();
  }, [load]);

  const simpan = async () => {
    setBusy(true);
    try {
      await api.post("/gudang/ledger", { ...form, nominal: Number(form.nominal) || 0 });
      toast.success(`${form.jenis === "masuk" ? "Pemasukan" : "Pengeluaran"} gudang tercatat`);
      setForm({ ...form, nominal: "", keterangan: "" });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="gudang-board">
      <div className="grid gap-4 sm:grid-cols-3">
        <Card label="Total Pemasukan" value={rp(led.total_masuk)} warna="text-emerald-300" />
        <Card label="Total Pengeluaran" value={rp(led.total_keluar)} warna="text-rose-300" />
        <Card label="Saldo Gudang" value={rp(led.saldo)} warna="text-amber-300" />
      </div>

      <div className="glass rounded-2xl p-5 md:p-6 space-y-3">
        <div className="flex flex-wrap items-end gap-3">
          <Field label="Jenis">
            <select data-testid="gudang-jenis" value={form.jenis} onChange={(e) => setForm({ ...form, jenis: e.target.value })} className={sel}>
              <option value="masuk">Pemasukan</option>
              <option value="keluar">Pengeluaran</option>
            </select>
          </Field>
          <Field label="Kategori">
            <Input data-testid="gudang-kategori" value={form.kategori} onChange={(e) => setForm({ ...form, kategori: e.target.value })} className="h-11 w-36 bg-slate-900/70 border-white/10" />
          </Field>
          <Field label="Nominal">
            <Input data-testid="gudang-nominal" value={form.nominal} onChange={(e) => setForm({ ...form, nominal: e.target.value })} className="h-11 w-32 bg-slate-900/70 border-white/10" />
          </Field>
          <Field label="Keterangan">
            <Input data-testid="gudang-keterangan" value={form.keterangan} onChange={(e) => setForm({ ...form, keterangan: e.target.value })} className="h-11 w-56 bg-slate-900/70 border-white/10" />
          </Field>
          <Field label="Tanggal">
            <Input data-testid="gudang-tanggal" type="date" value={form.tanggal} onChange={(e) => setForm({ ...form, tanggal: e.target.value })} className="h-11 bg-slate-900/70 border-white/10" />
          </Field>
          <Button data-testid="gudang-submit" onClick={simpan} disabled={busy || !form.nominal} className="h-11 rounded-xl bg-teal-600 hover:bg-teal-500 px-5">
            <Plus className="w-4 h-4 mr-2" /> Simpan
          </Button>
        </div>
      </div>

      <div className="glass rounded-2xl p-5 md:p-6 space-y-3" data-testid="gudang-summary">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <h4 className="font-display text-sm font-semibold uppercase tracking-wider text-slate-200">
            Tabel Pemasukan &amp; Pengeluaran Gudang
          </h4>
          <div className="flex items-end gap-3">
            <Field label="Dari Tanggal">
              <Input data-testid="gudang-filter-start" type="date" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} className="h-10 bg-slate-900/70 border-white/10" />
            </Field>
            <Field label="Sampai">
              <Input data-testid="gudang-filter-end" type="date" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} className="h-10 bg-slate-900/70 border-white/10" />
            </Field>
          </div>
        </div>
        <div className="overflow-x-auto rounded-xl border border-white/10 max-h-96 overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-900/80 text-slate-400 uppercase text-[10px] tracking-wider sticky top-0">
              <tr>
                {["No", "Tanggal", "Kategori", "Jenis", "Keterangan", "Nominal", "Sumber", "User Entri"].map((h) => (
                  <th key={h} className="px-3 py-2 text-left font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody data-testid="gudang-list">
              {led.rows.length === 0 && (
                <tr><td colSpan={8} className="px-3 py-6 text-center text-slate-500">Belum ada transaksi gudang.</td></tr>
              )}
              {led.rows.map((r) => (
                <tr key={r.id} className="border-t border-white/5">
                  <td className="px-3 py-2 text-slate-300">{r.nomor}</td>
                  <td className="px-3 py-2 text-slate-400">{fmtDate(r.tanggal)}</td>
                  <td className="px-3 py-2 text-slate-400">{r.kategori || "-"}</td>
                  <td className="px-3 py-2">
                    <span className={`rounded-lg px-2 py-1 text-[10px] font-semibold uppercase ${r.jenis === "masuk" ? "bg-emerald-500/15 text-emerald-300" : "bg-rose-500/15 text-rose-300"}`}>
                      {r.jenis}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-slate-400">{r.keterangan || "-"}</td>
                  <td className="px-3 py-2 font-semibold">{rp(r.nominal)}</td>
                  <td className="px-3 py-2 text-[10px] uppercase text-slate-500">{r.auto ? "otomatis (margin)" : "manual"}</td>
                  <td className="px-3 py-2 text-slate-500">{r.by}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
